<template>
    <div
        v-if="isLoading"
        class="mx-auto mt-12 max-w-screen-xl"
    >
        <skeleton />
    </div>

    <div v-else>
        <app-error v-if="data === undefined" />
        <app-error v-else-if="checkout === undefined" />
        <not-found v-else-if="checkout === null" />
        <purchase-contract-section v-else>
            <template #title> Checkout </template>
            <div
                class="grid grid-cols-1"
                :class="isKiosk ? '' : 'xl:grid-cols-2'"
            >
                <div :class="isKiosk ? '' : 'xl:justify-self-end'">
                    <div
                        :class="
                            isKiosk
                                ? ''
                                : 'max-w-[650px] sm:mx-auto xl:mx-0 xl:min-w-[650px] xl:max-w-[650px] xl:px-8 xl:py-12'
                        "
                    >
                        <nuxt-error-boundary @error="logError">
                            <checkout-form
                                :query="data"
                                :checkout="checkout"
                                :contract="contract"
                                :balance-due-mode="balanceDueMode"
                                @checkout-success="handleCheckoutSuccess"
                            />

                            <template #error>
                                <app-error />
                            </template>
                        </nuxt-error-boundary>
                    </div>
                </div>

                <div
                    class="order-first bg-gray-50"
                    :class="[
                        isKiosk ? '' : 'xl:order-2 xl:border-l xl:bg-white'
                    ]"
                >
                    <div
                        class="max-w-[650px]"
                        :class="
                            isKiosk
                                ? ''
                                : 'sm:mx-auto xl:mx-0 xl:min-w-[650px] xl:max-w-[650px] xl:px-8 xl:py-12'
                        "
                    >
                        <checkout-cart
                            :checkout="checkout"
                            :balance-due-mode="balanceDueMode"
                        />
                    </div>
                </div>
            </div>
        </purchase-contract-section>
    </div>
</template>

<script setup lang="ts">
import {
    useActiveFacility,
    useKioskSuccess,
    useLayoutMode,
    useStorefront
} from '#imports'
import { useQuery } from '@tanstack/vue-query'
import type { FragmentType } from '~/resources/graphql'
import { graphql } from '~/resources/graphql'
import { computed } from 'vue'
import skeleton from 'primevue/skeleton/Skeleton.vue'
import { useRpQuery } from '~/composables/graphql'
import CheckoutCart from '~/components/Checkout/Cart/CheckoutCart.vue'
import CheckoutForm from '~/components/Checkout/CheckoutForm.vue'
import NuxtErrorBoundary from '#app/components/nuxt-error-boundary'
import AppError from '~/components/NotFound/AppError.vue'
import { useLogError } from '~/composables/use-log-error'
import { CheckoutSuccessFragment } from '~/components/Checkout/CheckoutSuccess.vue'
import NotFound from '~/components/NotFound/NotFound.vue'
import { provideCheckoutPaymentData } from '~/components/Checkout/Sections/Payment/checkout-payment-data'
import { DocumentSectionContractFragment } from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import PurchaseContractSection from '~/components/PurchaseContract/PurchaseContractSection.vue'

const props = defineProps<{
    contract: FragmentType<typeof DocumentSectionContractFragment>
}>()

const activeFacility = useActiveFacility()

const { isKiosk } = useLayoutMode()
const logError = useLogError()

// TODO: Really need to fetch this in its own fragment
const contractId = computed(() => props.contract.id)
const query = useRpQuery()
const { data, isLoading } = useQuery({
    queryKey: ['InlineCheckoutQuery', contractId, activeFacility],
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query InlineCheckoutQuery($facilityId: ID!, $contractId: ID!) {
                    ...CheckoutFormQueryFragment
                    node(id: $contractId) {
                        __typename
                        ... on PlanContract {
                            inlineCheckout {
                                ...CheckoutFormCheckoutFragment
                                ...CheckoutCartFragment
                            }
                        }
                    }
                }
            `),
            {
                facilityId: activeFacility.value.id,
                contractId: contractId.value
            }
        )
})

const checkout = computed(() => {
    if (data.value === undefined) {
        return undefined
    } else if (data.value.node === null) {
        return null
    } else if (data.value.node.__typename !== 'PlanContract') {
        return null
    } else {
        return data.value.node.inlineCheckout
    }
})

provideCheckoutPaymentData()

const goToSuccess = useKioskSuccess()

function handleCheckoutSuccess(
    data: FragmentType<typeof CheckoutSuccessFragment>
) {
    goToSuccess({
        includedInvoice: true,
        wasGuest: false,
        data
    })
}

const storefront = useStorefront()
const balanceDueMode = computed<boolean>(
    () => isKiosk.value && !storefront.value.collectMoney
)
</script>
